import React, { useState } from "react";
import {
  PencilSimple,
  Trash,
  CalendarBlank,
  VideoCamera,
  MapPinArea,
  ChatCircleText,
  ArrowRight,
  CaretDown,
  CaretRight,
} from "@phosphor-icons/react";

/**
 * Collapsible event card used on the customer Events tab.
 * Receives the event payload and onEdit / onDelete callbacks.
 */
export default function EventCard({ event, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const dt = new Date(event.date_time);
  return (
    <div
      className="bg-white border border-slate-200 rounded-md overflow-hidden"
      data-testid={`event-card-${event.id}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
        data-testid={`event-toggle-${event.id}`}
      >
        <div className="shrink-0 w-10 h-10 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center">
          {event.engagement_type === "Physical" ? (
            <MapPinArea size={18} weight="duotone" />
          ) : (
            <VideoCamera size={18} weight="duotone" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 truncate">
              {event.title || `${event.engagement_type} engagement`}
            </span>
            <span
              className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                event.engagement_type === "Physical"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-blue-50 text-blue-700"
              }`}
            >
              {event.engagement_type}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
            <CalendarBlank size={12} /> {dt.toLocaleString()}
            {event.place && (
              <>
                <span>·</span>
                <span className="truncate">{event.place}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span
            onClick={(e) => {
              e.stopPropagation();
              onEdit(event);
            }}
            className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-slate-100 cursor-pointer"
            data-testid={`event-edit-${event.id}`}
          >
            <PencilSimple size={14} />
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              onDelete(event);
            }}
            className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
            data-testid={`event-delete-${event.id}`}
          >
            <Trash size={14} />
          </span>
          {open ? (
            <CaretDown size={14} className="text-slate-400 ml-1" />
          ) : (
            <CaretRight size={14} className="text-slate-400 ml-1" />
          )}
        </div>
      </button>
      {open && (
        <div className="px-4 py-4 border-t border-slate-100 bg-slate-50/50 space-y-3 text-sm">
          {event.remarks && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 inline-flex items-center gap-1.5">
                <ChatCircleText size={11} /> Remarks
              </div>
              <p className="mt-1 text-slate-700 whitespace-pre-wrap">{event.remarks}</p>
            </div>
          )}
          {event.minutes_of_meeting && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Minutes of Meeting
              </div>
              <p className="mt-1 text-slate-700 whitespace-pre-wrap">
                {event.minutes_of_meeting}
              </p>
            </div>
          )}
          {event.next_steps && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-700 inline-flex items-center gap-1.5">
                <ArrowRight size={11} weight="bold" /> Next Steps
              </div>
              <p className="mt-1 text-slate-700 whitespace-pre-wrap">{event.next_steps}</p>
            </div>
          )}
          {!event.remarks && !event.minutes_of_meeting && !event.next_steps && (
            <div className="text-slate-400 italic text-xs">No additional details</div>
          )}
        </div>
      )}
    </div>
  );
}
